import { useCallback, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { startLoader, stopLoader } from "../../../Core/Loader/loaderUtils";
import { formatSessionDate } from "../../../utils/helper";
import { useFetchDemos, useFetchGuestDashboardStats } from "../services/Api";
import EditClassFlow from "./index";

/**
 * AppEditDemoContainer — the native app's entry into the edit-demo flow.
 *
 * Opened in a webview at #/edit-demo?slotId=<SlotId>&studentId=<id> after the
 * /native handshake. Renders nothing but the EditClassFlow popup for that one
 * demo, then hands control back to the app.
 *
 * The done-step popups reload the page (window.location.reload); after a
 * reschedule or cancel the old SlotId no longer appears in /student/demos
 * (cancel drops the row, reschedule moves the booking to a new slot), so the
 * reloaded page finds no session and returns to the app. That missing-slot
 * exit is the flow's normal way out, not an error path.
 */
const AppEditDemoContainer = () => {
  const location = useLocation();
  const slotId = useMemo(
    () => Number(new URLSearchParams(location.search).get("slotId")),
    [location.search],
  );

  const { apiData: demosData, loading: demosLoading } = useFetchDemos();
  const { apiData: statsData, loading: statsLoading } =
    useFetchGuestDashboardStats();
  const loading = demosLoading || statsLoading;

  const session = useMemo(
    () =>
      (demosData?.upcomingSessions ?? []).find(
        (row) => row?.SlotId === slotId,
      ) ?? null,
    [demosData, slotId],
  );

  const returnToApp = useCallback(() => {
    const returnUrl = localStorage.getItem("appReturnUrl") || "goBack";
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({ type: "route", url: returnUrl }),
    );
  }, []);

  const bookAgainInApp = useCallback(() => {
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({ type: "bookDemo" }),
    );
  }, []);

  useEffect(() => {
    if (loading) startLoader();
    else stopLoader();
  }, [loading]);

  useEffect(() => {
    if (!loading && !session) returnToApp();
  }, [loading, session, returnToApp]);

  if (loading || !session) return null;

  const [datePart = "", timePart = ""] =
    formatSessionDate(session?.localStartTime)?.split(" • ") ?? [];

  return (
    <EditClassFlow
      open={true}
      onClose={returnToApp}
      session={session}
      initialTimezone={statsData?.student?.timeZone || session?.studentTimeZone}
      onBookAgain={bookAgainInApp}
      trialClass={{
        date: datePart,
        time: timePart,
        subject: session?.courseName,
        mentorName: session?.mentorName,
        mentorAvatar: session?.mentorImage ?? undefined,
      }}
    />
  );
};

export default AppEditDemoContainer;
